import React from "react";

import PropTypes from "prop-types";

import { ATTACHMENT_TYPES } from "manager/config/constants";
import Settings from "shared/components/Settings";
import Skeleton from "shared/components/Skeleton";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import ApplicationSetupHeader from "../Header";
import { useAttachmentValidation } from "../DealSetupForm/AttachmentValidationContext";
import { ConsolidatedAttachmentGrid } from "../ConsolidatedAttachmentGrid";
import {
  buildApplicantTypes,
  buildItemDisplayLabel,
  getAttachmentsFilter,
  mapFinancialDocLabels,
  useAttachmentHandlers,
  useDocQuantitiesUiEnabled,
  useGuardedIncomeDocsToggle,
} from "../utils";
import { IncomeSurfaceGate } from "../IncomeSurfaceGate";
import RequiredAttachmentsUnverifiedIncomeLegacy from "./RequiredAttachmentsUnverifiedIncomeLegacy";

const APPLICANT_TYPES_CONFIG = buildApplicantTypes({
  applicant: "requiresApplicantAdditionalAttachementsForIncome",
  occupant: "requiresOccupantAdditionalAttachementsForIncome",
  guarantor: "requiresGuarantorAdditionalAttachementsForIncome",
});

const ATTACHMENT_TYPE = ATTACHMENT_TYPES.UNVERIFIED_INCOME;
const getRequiredAttachments = getAttachmentsFilter(ATTACHMENT_TYPE);

const RequiredAttachmentsUnverifiedIncome = ({ loading, disabled, forSettingsLabel }) => {
  const uiEnabled = useDocQuantitiesUiEnabled();
  if (!uiEnabled) {
    return (
      <RequiredAttachmentsUnverifiedIncomeLegacy
        loading={loading}
        disabled={disabled}
        forSettingsLabel={forSettingsLabel}
      />
    );
  }

  return (
    <RequiredAttachmentsUnverifiedIncomeGrid
      loading={loading}
      disabled={disabled}
    />
  );
};

const RequiredAttachmentsUnverifiedIncomeGrid = ({ loading, disabled }) => {
  const { reportWarnings } = useAttachmentValidation();
  const isRunwayEnabled = useVeroFlag(VeroFlag.useDocumentRunwayCalculation);
  const isConsolidated = useDocQuantitiesUiEnabled();
  // PRODUCT-1005 rename: when the V1 workflow is consolidated (single
  // `income-documents` task instead of split financial/unverified), the
  // section that used to edit "unverified income" now edits the unified
  // list. Reflect that in the header.
  const sectionTitle = isConsolidated
    ? "Income Documents"
    : "Unverified Income Setup";
  const sectionSubTitle = isConsolidated
    ? "Manage the income documents you want to require from applicants, occupants and guarantors."
    : "Please manage the attachments that you want to require from applicants, occupants and guarantors in case their income & balances are not verified";
  const {
    values,
    onChangeAttachment,
    onChangeQuantity,
    onChangeRunway,
    handleAddCustomDoc,
    handleDeleteDoc,
  } = useAttachmentHandlers({
    attachmentType: ATTACHMENT_TYPE,
    personas: APPLICANT_TYPES_CONFIG,
  });

  const allItems = APPLICANT_TYPES_CONFIG.flatMap((at) =>
    mapFinancialDocLabels(getRequiredAttachments(values, at.applicantType))
  );
  const onGuardedTogglePersona = useGuardedIncomeDocsToggle({
    surfaceFamily: "income",
  });

  return (
    <div id="unverified-income-grid">
      <Settings
        header={
          <Skeleton loading={loading}>
            <ApplicationSetupHeader
              title={sectionTitle}
              subTitle={sectionSubTitle}
            />
          </Skeleton>
        }
        nodes={[
          {
            key: 1,
            component: (
              <Skeleton loading={loading}>
                <IncomeSurfaceGate>
                  <ConsolidatedAttachmentGrid
                    personas={APPLICANT_TYPES_CONFIG}
                    items={allItems}
                    values={values}
                    onTogglePersona={onGuardedTogglePersona}
                    onToggleItem={onChangeAttachment}
                    onChangeQuantity={onChangeQuantity}
                    onAddCustomDoc={handleAddCustomDoc}
                    onDeleteDoc={handleDeleteDoc}
                    buildLabel={buildItemDisplayLabel}
                    sectionTitle={sectionTitle}
                    sectionId="unverified-income-grid"
                    onValidationChange={(w) =>
                      reportWarnings("unverifiedIncome", w)
                    }
                    disabled={disabled}
                    onChangeRunway={onChangeRunway}
                    isRunwayEnabled={isRunwayEnabled}
                  />
                </IncomeSurfaceGate>
              </Skeleton>
            ),
          },
        ]}
      />
    </div>
  );
};

RequiredAttachmentsUnverifiedIncome.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachmentsUnverifiedIncome.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachmentsUnverifiedIncome;
